// [2026-09-02] ADDED — the three cross-tenant feeds: API requests, proctoring sessions, audit log.
//
// THEY LIVE IN ONE FILE because they are the same component three times over: a server-paginated
// table with filters and a drill-down. Splitting them would triple the boilerplate without
// separating anything that changes independently.
//
// EVERY ROW CARRIES ITS TENANT. That is the difference between these and the customer's own screens
// — an operator reading a failure needs to know WHOSE it is before anything else, so client_id is a
// column here and a filter, never an assumption.

import { useEffect, useState } from 'react'
import { Empty, Modal, Notice, Skeleton, num, ms, when } from '@incruiter/proctoring-client-frontend'
import { Badge } from '@incruiter/proctoring-client-frontend/src/components/ui/badge.jsx'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@incruiter/proctoring-client-frontend/src/components/ui/table.jsx'
import DataTable from '../DataTable.jsx'
// [2026-09-03] CHANGED — adminEvidenceObjectUrl added. OLD: import { admin } from '../api.js'
// [2026-09-07] CHANGED — adminProofObjectUrl added for the analysis report's frames.
// OLD: import { admin, adminEvidenceObjectUrl } from '../api.js'
import { admin, adminEvidenceObjectUrl, adminProofObjectUrl } from '../api.js'

const CLASS_VARIANT = { success: 'healthy', client_error: 'degraded', server_error: 'critical' }
const STATE_VARIANT = { active: 'healthy', paused: 'degraded', ended: 'idle' }
const SEVERITY_VARIANT = { high: 'critical', medium: 'serious', low: 'degraded' }

// ── the raw API call feed ─────────────────────────────────────────────────────────────

export function RequestsFeed({ onError }) {
  const [retention, setRetention] = useState(null)
  const [row, setRow] = useState(null)

  async function fetchPage({ limit, offset, status_class, client_id }) {
    try {
      // NOTE: no `search` — GET /admin/v1/requests does not accept one (it takes client_id,
      // status_class and endpoint). Passing a search box that the API ignores would be a control
      // that looks like it works and does nothing, so this table filters instead of searching.
      const d = await admin.requests({ limit, offset, status_class, client_id })
      if (d.retention_days && d.retention_days !== retention) setRetention(d.retention_days)
      return { rows: d.requests || [], total: d.total || 0 }
    } catch (err) { onError?.(err); throw err }
  }

  return (
    <>
      <DataTable
        title="API requests — every tenant"
        subtitle={retention
          ? `One row per API call, newest first · kept for ${retention} days`
          : 'One row per API call, newest first'}
        columns={[
          { key: 'ts', label: 'When', render: (r) => <span className="muted">{when(r.ts)}</span> },
          { key: 'client_id', label: 'Tenant',
            render: (r) => <span className="mono" style={{ fontSize: 12 }}>{r.client_id}</span> },
          { key: 'endpoint', label: 'Endpoint',
            render: (r) => <span className="mono">{r.endpoint}</span> },
          { key: 'status_code', label: 'Status',
            render: (r) => <Badge variant={CLASS_VARIANT[r.status_class] || 'idle'}>{r.status_code}</Badge> },
          { key: 'latency_ms', label: 'Latency', align: 'right',
            render: (r) => (r.latency_ms == null ? '—' : ms(r.latency_ms)) },
        ]}
        fetchPage={fetchPage}
        searchPlaceholder="(filter below)"
        filters={[{
          key: 'status_class', label: 'Status',
          options: [
            { value: 'success', label: 'Success (2xx)' },
            { value: 'client_error', label: 'Client error (4xx)' },
            { value: 'server_error', label: 'Server error (5xx)' },
          ],
        }]}
        onRowClick={setRow}
        rowKey={(r, i) => `${r.ts}-${r.client_id}-${i}`}
        emptyTitle="No API calls in the retention window"
        emptyHint="Calls appear here as tenants use their keys."
        csvName="admin-requests"
      />
      {row && (
        <Modal title="Request detail" subtitle={row.endpoint} onClose={() => setRow(null)}>
          <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            {[['When', when(row.ts)], ['Tenant', row.client_id], ['Endpoint', row.endpoint],
              ['Status', `${row.status_code} (${row.status_class})`],
              ['Latency', row.latency_ms == null ? '—' : ms(row.latency_ms)],
              ['Credits', String((row.credits_milli || 0) / 1000)],
              ['Job id', row.job_id || '—']].map(([k, v]) => (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 10 }}>
                <span className="muted">{k}</span><span className="mono">{v}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  )
}

// ── proctoring sessions, across every tenant ──────────────────────────────────────────

// [2026-09-03] ADDED — one captured screenshot.
//
// The staff report had the same gap the customer one did: it counted evidence and showed no
// picture, because nothing served the BYTES to a browser. The other half is the new
// GET /admin/v1/sessions/{id}/evidence/{event_id}.
//
// ABORT AND REVOKE ON UNMOUNT, both. Closing the modal mid-load must cancel the request, and an
// object URL pins a decoded image in memory until revoked — an operator clicking through twenty
// sessions would otherwise accumulate every frame of all of them for the life of the page.
function EvidenceFrame({ sessionId, frame }) {
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    let objectUrl = null
    adminEvidenceObjectUrl(sessionId, frame.event_id, controller.signal)
      .then((value) => { objectUrl = value; setUrl(value) })
      .catch((err) => { if (err.name !== 'AbortError') setFailed(true) })
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [sessionId, frame.event_id])

  return (
    <figure className="evidence-card">
      <div className="evidence-thumb">
        {failed
          // Stated, not left as a broken-image icon. A pointer outliving its blob is a real case —
          // storage lifecycle rules expire frames.
          ? <div className="evidence-placeholder muted">Frame unavailable</div>
          : url
            ? <img src={url} alt={`Captured ${frame.kind || 'screen'} frame`} loading="lazy" />
            : <Skeleton height={120} />}
      </div>
      <figcaption>
        <span>{String(frame.kind || 'screen').replace(/_/g, ' ')}</span>
        <br />
        <span className="muted">{when(frame.captured_at)}</span>
      </figcaption>
    </figure>
  )
}

function SessionReport({ sessionId, onClose, onError }) {
  const [state, setState] = useState({ report: null, error: null, loading: true })

  // IN AN EFFECT, NOT IN THE RENDER BODY. Fetching during render fires on every re-render and
  // setState from render is a loop — the modal would hammer the API for as long as it was open.
  useEffect(() => {
    let live = true
    setState({ report: null, error: null, loading: true })
    admin.session(sessionId)
      .then((report) => { if (live) setState({ report, error: null, loading: false }) })
      .catch((err) => {
        if (!live) return
        onError?.(err)
        setState({ report: null, error: err, loading: false })
      })
    return () => { live = false }
  }, [sessionId, onError])

  const { report, error, loading } = state
  const session = report?.session || {}
  const counts = report?.counts || {}

  return (
    <Modal wide title="Session report" subtitle={session.reference_id || sessionId} onClose={onClose}>
      {loading && <Skeleton height={200} />}
      {error && <Notice kind="error">{error.message}</Notice>}
      {report && (
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gap: 10,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            {[['Tenant', session.client_id], ['State', session.state],
              ['Events', num(counts.events || 0)], ['Evidence', num(counts.evidence || 0)],
              ['Started', when(session.created_at)],
              ['Ended', session.ended_at ? when(session.ended_at) : '—']].map(([k, v]) => (
              <div key={k}>
                <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase',
                                                letterSpacing: '.06em' }}>{k}</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>{v ?? '—'}</div>
              </div>
            ))}
          </div>

          {/* The API's own honesty note about browser telemetry — rendered, not dropped. An operator
              deciding about a real candidate must see the limitation beside the evidence. */}
          {report.trust_note && <Notice kind="warn">{report.trust_note}</Notice>}

          {/* [2026-09-03] ADDED — the screenshots, above the timeline. An operator opens this modal
              to judge what a candidate actually did, and the picture is the evidence that judgement
              rests on; the timeline is the supporting detail and it is longer, so putting the images
              under it would mean scrolling past twenty rows to reach the thing you came for. */}
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
              Screenshots ({num((report.evidence || []).length)})
            </div>
            {(report.evidence || []).length === 0 ? (
              // Said plainly rather than rendering an empty grid — "none captured" and "they failed
              // to load" are different problems and blank space looks like the second one.
              <Empty title="No screenshots captured"
                     hint="This session recorded events but no image frames." />
            ) : (
              <div className="evidence-grid">
                {report.evidence.map((frame) => (
                  <EvidenceFrame key={frame.event_id} sessionId={sessionId} frame={frame} />
                ))}
              </div>
            )}
          </div>

          <div>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
              Timeline ({num((report.events || []).length)} events)
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead><TableHead>Type</TableHead>
                    <TableHead>Severity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(report.events || []).map((e) => (
                    <TableRow key={e.event_id}>
                      <TableCell className="muted">{when(e.occurred_at)}</TableCell>
                      <TableCell>{String(e.type).replace(/_/g, ' ')}</TableCell>
                      <TableCell>
                        <Badge variant={SEVERITY_VARIANT[e.severity] || 'idle'}>{e.severity}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export function SessionsFeed({ onError }) {
  const [openId, setOpenId] = useState(null)

  async function fetchPage({ limit, offset, state, client_id }) {
    try {
      const d = await admin.sessions({ limit, offset, state, client_id })
      return { rows: d.sessions || [], total: d.total || 0 }
    } catch (err) { onError?.(err); throw err }
  }

  return (
    <>
      <DataTable
        title="Proctoring sessions — every tenant"
        subtitle="Click a row for the full candidate timeline"
        columns={[
          { key: 'reference_id', label: 'Candidate ref',
            render: (r) => <span className="mono">{r.reference_id || `${r.session_id.slice(0, 14)}…`}</span> },
          { key: 'client_id', label: 'Tenant',
            render: (r) => <span className="mono" style={{ fontSize: 12 }}>{r.client_id}</span> },
          { key: 'state', label: 'State',
            render: (r) => <Badge variant={STATE_VARIANT[r.state] || 'idle'}>{r.state}</Badge> },
          { key: 'events_received', label: 'Events', align: 'right',
            render: (r) => num(r.events_received || 0) },
          { key: 'created_at', label: 'Started',
            render: (r) => <span className="muted">{when(r.created_at)}</span> },
        ]}
        fetchPage={fetchPage}
        searchPlaceholder="(filter below)"
        filters={[{
          key: 'state', label: 'State',
          options: [{ value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' },
                    { value: 'ended', label: 'Ended' }],
        }]}
        onRowClick={(r) => setOpenId(r.session_id)}
        rowKey={(r) => r.session_id}
        emptyTitle="No sessions yet"
        emptyHint="Sessions appear as candidates start proctored exams."
        csvName="admin-sessions"
      />
      {openId && (
        <SessionReport key={openId} sessionId={openId}
                       onClose={() => setOpenId(null)} onError={onError} />
      )}
    </>
  )
}

// ── the audit log ─────────────────────────────────────────────────────────────────────

export function AuditLog({ onError }) {
  const [actions, setActions] = useState([])

  async function fetchPage({ limit, offset, action, client_id }) {
    try {
      const d = await admin.audit({ limit, offset, action, client_id })
      if ((d.actions || []).length && d.actions.length !== actions.length) setActions(d.actions)
      return { rows: d.entries || [], total: d.total || 0 }
    } catch (err) { onError?.(err); throw err }
  }

  return (
    <DataTable
      title="Audit log"
      subtitle="Who changed what, and when — every write an operator made"
      columns={[
        { key: 'ts', label: 'When',
          render: (r) => <span className="muted">{new Date((r.ts || 0) * 1000).toLocaleString()}</span> },
        { key: 'action', label: 'Action',
          render: (r) => <span className="mono" style={{ fontSize: 12 }}>{r.action}</span> },
        { key: 'actor_email', label: 'Actor',
          render: (r) => (
            <div>
              <div style={{ fontSize: 13 }}>{r.actor_name || r.actor_email}</div>
              <div className="muted" style={{ fontSize: 11.5 }}>{r.actor_role}</div>
            </div>
          ) },
        { key: 'client_id', label: 'Tenant',
          render: (r) => (r.client_id
            ? <span className="mono" style={{ fontSize: 12 }}>{r.client_id}</span>
            : <span className="muted">—</span>) },
        { key: 'detail', label: 'Detail',
          render: (r) => (
            <span className="muted" style={{ fontSize: 12 }}>
              {r.detail && Object.keys(r.detail).length ? JSON.stringify(r.detail).slice(0, 80) : '—'}
            </span>
          ) },
      ]}
      fetchPage={fetchPage}
      searchPlaceholder="(filter below)"
      filters={actions.length
        ? [{ key: 'action', label: 'Action',
             options: actions.map((a) => ({ value: a, label: a })) }]
        : []}
      rowKey={(r, i) => `${r.ts}-${r.action}-${i}`}
      emptyTitle="Nothing recorded yet"
      emptyHint="Every sign-in, grant, key issue and suspension is written here."
      csvName="admin-audit"
    />
  )
}

// ── the analysis API's jobs, across every tenant ──────────────────────────────────────
//
// [2026-09-07] ADDED — the staff view of the OTHER half of the product.
//
// This dashboard could already show a tenant's BROWSER sessions and nothing at all about the
// recorded-video analyses, even though a customer buys both. So the question support is actually
// asked — "the candidate's proctoring report never arrived, what happened?" — was answerable for
// the SDK half and not for the half billed per analysis. Staff had to read the customer's own
// dashboard back to them.
//
// IT MIRRORS SessionsFeed DELIBERATELY, down to the column order and the drill-down. An operator
// moving between the two tabs is doing the same job on different data; making them behave
// differently would be a second thing to learn for no gain.

const VIDEO_STATUS_VARIANT = {
  completed: 'healthy', processing: 'degraded', queued: 'idle',
  failed: 'critical', error: 'critical',
}

// The three interval kinds the engine reports, in the order a reviewer reads them: where the
// candidate looked, whether a face was there at all, then whether a second device was visible.
// Named here rather than derived from the payload so a new key upstream does not silently appear
// on this screen with a machine name for a label.
const INTERVAL_KINDS = [
  { key: 'looking_away_intervals', label: 'Looking away' },
  { key: 'face_issue_intervals', label: 'Face issue' },
  { key: 'device_intervals', label: 'Device detected' },
]

export function VideoJobsFeed({ onError }) {
  const [openId, setOpenId] = useState(null)

  async function fetchPage({ limit, offset, request_source, client_id }) {
    try {
      const d = await admin.videoJobs({ limit, offset, request_source, client_id })
      return { rows: d.jobs || [], total: d.total || 0 }
    } catch (err) { onError?.(err); throw err }
  }

  return (
    <>
      <DataTable
        title="Proctoring analyses — every tenant"
        subtitle="Click a row for the finished report"
        columns={[
          { key: 'job_id', label: 'Job',
            render: (r) => <span className="mono">{r.job_id}</span> },
          { key: 'client_id', label: 'Tenant',
            render: (r) => <span className="mono" style={{ fontSize: 12 }}>{r.client_id}</span> },
          { key: 'status', label: 'Status',
            render: (r) => (
              <Badge variant={VIDEO_STATUS_VARIANT[r.status] || 'idle'}>{r.status || 'unknown'}</Badge>
            ) },
          { key: 'request_source', label: 'Source',
            render: (r) => <span className="muted">{r.request_source || '—'}</span> },
          { key: 'interview_id', label: 'Interview',
            render: (r) => <span className="mono" style={{ fontSize: 12 }}>{r.interview_id || '—'}</span> },
          { key: 'created_at', label: 'Submitted',
            render: (r) => <span className="muted">{when(r.created_at)}</span> },
        ]}
        fetchPage={fetchPage}
        searchPlaceholder="(filter below)"
        filters={[{
          key: 'request_source', label: 'Source',
          options: [{ value: 'incbot', label: 'IncBot' }, { value: 'incscreen', label: 'IncScreen' }],
        }]}
        onRowClick={(r) => setOpenId(r.job_id)}
        rowKey={(r) => r.job_id}
        emptyTitle="No analyses yet"
        emptyHint="Rows appear when a tenant submits a recording to the proctoring engine."
        csvName="admin-video-jobs"
      />
      {openId && (
        <VideoReport key={openId} jobId={openId}
                     onClose={() => setOpenId(null)} onError={onError} />
      )}
    </>
  )
}

// One proof frame. Same abort-and-revoke contract as EvidenceFrame above, and for the same reason:
// an operator clicking through twenty reports would otherwise pin every decoded frame in memory
// for the life of the page.
function ProofFrame({ jobId, kind, index }) {
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    let objectUrl = null
    adminProofObjectUrl(jobId, kind, index, controller.signal)
      .then((value) => { objectUrl = value; setUrl(value) })
      .catch((err) => { if (err.name !== 'AbortError') setFailed(true) })
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [jobId, kind, index])

  if (failed) return <div className="evidence-placeholder muted">Frame unavailable</div>
  if (!url) return <Skeleton height={90} />
  return <img src={url} alt={`Proof frame ${index}`} loading="lazy" />
}

// [2026-09-16] ADDED — the Start column was printing a raw timestamp with an "s" glued on.
//
// WHAT WAS ON SCREEN: `2026-09-13T11:00:20s`. The cell reads `r.start_sec ?? r.start`, and
// `start_sec` DOES NOT EXIST — verified against the real report for job d2d331f0 on 2026-09-16,
// whose interval keys are start, end, duration_sec, evidence_score, source, proof_timestamp,
// index, has_proof. So it always fell through to `start`, an ISO timestamp, and the "s" suffix
// then claimed it was a number of seconds.
//
// AND THE TIMESTAMP ITSELF IS NOT A CLOCK TIME. The engine stamps frames from a SYNTHETIC ORIGIN:
// that job's frames run from exactly 11:00:00 to exactly 11:30:00 while the recording's own
// filename carries the epoch 11:04:18. Printing it as a wall clock tells a reviewer the candidate
// did this at eleven o'clock, which is not a fact we hold.
//
// So the cell now shows the OFFSET INTO THE RECORDING, measured from `recording_start` — the first
// frame's timestamp, which app/core/video_processing_proxy.py:summarise_report returns for exactly
// this purpose. That is both true and the number a reviewer types into the video player. This is
// the same correction made to the customer dashboard on the same date.
//
// FALLS BACK RATHER THAN FAILING: with no `recording_start` (an engine payload that carried no
// frames) the raw value is shown as it was before, minus the misleading "s".
function offsetInto(start, origin) {
  if (typeof start !== 'string') return '\u2014'
  const at = new Date(start).getTime()
  const zero = origin ? new Date(origin).getTime() : NaN
  if (!Number.isFinite(at)) return String(start)
  if (!Number.isFinite(zero)) return String(start)
  const total = Math.max(Math.round((at - zero) / 1000), 0)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const sec = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
}

function VideoReport({ jobId, onClose, onError }) {
  const [state, setState] = useState({ report: null, error: null, loading: true })

  // In an effect, not the render body — see SessionReport above for why.
  useEffect(() => {
    let live = true
    setState({ report: null, error: null, loading: true })
    admin.videoReport(jobId)
      .then((report) => { if (live) setState({ report, error: null, loading: false }) })
      .catch((err) => {
        if (!live) return
        onError?.(err)
        setState({ report: null, error: err, loading: false })
      })
    return () => { live = false }
  }, [jobId, onError])

  const { report, error, loading } = state
  const totals = report?.totals || {}

  return (
    <Modal title={`Analysis ${jobId}`} onClose={onClose} wide>
      {loading && <Skeleton height={200} />}

      {/* A 404 here is the ORDINARY case, not a fault: the report does not exist until the engine
          finishes and delivers it by webhook. Saying so is the difference between an operator
          waiting and an operator raising a bug. */}
      {error && (
        <Notice tone="info">
          No finished report for this analysis yet. Reports arrive by webhook when the engine
          completes; the job row shows where it currently is.
        </Notice>
      )}

      {report && (
        <>
          <div className="grid cols-4" style={{ marginBottom: 16 }}>
            {[['Room', report.room_id || '—'],
              ['Frames', num(report.frame_count || 0)],
              ['Drowsiness', report.drowsiness_percent == null
                ? '—' : `${report.drowsiness_percent}%`],
              ['Flagged intervals',
               num(INTERVAL_KINDS.reduce((n, k) => n + (totals[k.key]?.count || 0), 0))],
            ].map(([label, value]) => (
              <div className="tile" key={label}>
                <div className="tile-label">{label}</div>
                <div className="num">{value}</div>
              </div>
            ))}
          </div>

          {INTERVAL_KINDS.map(({ key, label }) => {
            const rows = report[key] || []
            return (
              <section key={key} style={{ marginBottom: 18 }}>
                <h3 style={{ marginBottom: 8 }}>
                  {label}{' '}
                  <span className="muted" style={{ fontWeight: 400 }}>
                    {rows.length} · {totals[key]?.duration_sec || 0}s total
                  </span>
                </h3>
                {rows.length === 0
                  ? <div className="muted">Nothing flagged.</div>
                  : (
                    <div className="table-wrap">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Start</TableHead>
                            <TableHead>Duration</TableHead>
                            <TableHead>Proof</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((r) => (
                            <TableRow key={r.index}>
                              {/* [2026-09-16] CHANGED — an offset into the recording, not a raw
                                  timestamp with an "s" stuck on it. See offsetInto.
                                  OLD: <TableCell className="mono">{r.start_sec ?? r.start ?? '—'}s</TableCell> */}
                              <TableCell className="mono"
                                         title={r.start ? `engine timestamp ${r.start}` : undefined}>
                                {offsetInto(r.start, report.recording_start)}
                              </TableCell>
                              <TableCell>{r.duration_sec ?? '—'}s</TableCell>
                              <TableCell>
                                {/* Rendered ONLY where has_proof says a frame exists — the flag is
                                    what the report carries in place of the image, so a thumbnail is
                                    never requested for an interval that has none. */}
                                {r.has_proof
                                  ? <div className="evidence-thumb" style={{ maxWidth: 160 }}>
                                      <ProofFrame jobId={jobId} kind={key} index={r.index} />
                                    </div>
                                  : <span className="muted">—</span>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
              </section>
            )
          })}

          {/* The counterpart of the trust note on the browser report. Different limitation, same
              rule: a reviewer deciding about a real candidate must not have to already know it. */}
          <Notice tone="info">
            These findings come from automated analysis of the recording. They are evidence for a
            human decision, not a verdict.
          </Notice>
        </>
      )}
      {!loading && !error && !report && <Empty title="Nothing to show" />}
    </Modal>
  )
}
